import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { HeaderItem } from "../../types/menu.type";
import Badge from "../badge";

const HeaderLink: React.FC<{ item: HeaderItem }> = ({ item }) => {
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const location = useLocation();

  const { t } = useTranslation("menu");

  const ref = useRef<HTMLDivElement>(null);

  /*
   * Check whether the current menu item is active.
   */
  const isActive =
    location.pathname === item.href ||
    item.submenu?.some((group) =>
      group.items.some(
        (subItem) =>
          location.pathname.startsWith(subItem.href) ||
          subItem.subItems?.some((child) =>
            location.pathname.startsWith(child.href)
          )
      )
    );

  /*
   * Open/close submenu.
   */
  const handleClick = (e: React.MouseEvent) => {
    if (item.submenu) {
      e.preventDefault();

      setSubmenuOpen((prev) => !prev);
    }
  };

  /*
   * Close submenu when clicking outside.
   */
  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node)
      ) {
        setSubmenuOpen(false);
      }
    }

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  /*
   * Tailwind-safe grid columns.
   */
  const gridCols: Record<number, string> = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
  };

  return (
    <div ref={ref} className="relative">

      {/* Main Link */}
      <Link
        to={item.href}
        onClick={handleClick}
        className={`
          flex items-center gap-1
          relative font-medium capitalize
          transition-colors duration-200

          ${
            isActive
              ? "dark:text-primary text-tealGreen border-b-3"
              : "text-dark dark:text-white hover:text-tealGreen dark:hover:text-primary"
          }
        `}
      >
        {/* Badge */}
        {item.badge && <Badge />}

        {item.upgrade && (
          <Badge text="upgrade" />
        )}

        {/* Main Menu Translation */}
        {t(item.label)}

        {/* Dropdown Arrow */}
        {item.submenu && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1.2em"
            height="1.2em"
            viewBox="0 0 24 24"
            className={`
              transition-transform duration-200
              ${submenuOpen ? "rotate-180" : ""}
            `}
          >
            <path
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="m7 10l5 5l5-5"
            />
          </svg>
        )}
      </Link>

      {/* Mega Menu */}
      {submenuOpen && item.submenu && (
        <div
          className={`
            absolute left-1/2
            -translate-x-1/2
            top-5
            overflow-y-auto
            mt-3
            w-auto

            ${
              item.submenu.length > 2
                ? "min-w-[60rem]"
                : "min-w-[35rem]"
            }

            bg-white
            dark:bg-darklight
            shadow-lg
            rounded-2xl
            p-6
            z-50
            text-darkmode
            dark:text-white
          `}
          data-aos="fade-down"
          data-aos-duration="300"
        >
          <ul
            className={`
              grid gap-6
              ${
                item.submenu.length >= 2
                  ? gridCols[item.submenu.length + 1] ||
                    "grid-cols-2"
                  : "grid-cols-2"
              }
            `}
          >
            {item.submenu.map(
              (group, groupIndex) => (
                <li
                  key={groupIndex}
                  className="px-4 last:border-none"
                >
                  {/* Group Title */}
                  {group.title && (
                    <span
                      className="
                        block pb-3
                        text-sm
                        text-gray-400
                        dark:text-white
                        font-semibold
                        uppercase
                      "
                    >
                      {t(group.title)}
                    </span>
                  )}

                  {/* Group Links */}
                  {group.items.map(
                    (subItem, index) => {
                      const isSubActive =
                        location.pathname ===
                        subItem.href;

                      return (
                        <div
                          key={index}
                          className="relative group/item"
                        >
                          <Link
                            to={subItem.href}
                            onClick={() =>
                              setSubmenuOpen(false)
                            }
                            className={`
                              flex items-center gap-2
                              p-3 rounded-lg
                              transition-colors
                              text-sm font-medium
                              group

                              ${
                                isSubActive
                                  ? "bg-primary text-midnight_text"
                                  : "text-gray-800 dark:text-muted hover:bg-brand-50"
                              }
                            `}
                          >
                            {/* Submenu Translation */}
                            {t(subItem.label)}

                            {/* Right Arrow */}
                            {subItem.subItems && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="none"
                                viewBox="0 0 24 24"
                                className="ml-auto stroke-brand-700"
                              >
                                <path
                                  stroke="currentColor"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            )}
                          </Link>

                          {/* Nested Dropdown */}
                          {subItem.subItems && (
                            <div
                              className="
                                absolute
                                left-full
                                top-0
                                ml-2
                                hidden
                                group-hover/item:block
                                bg-white
                                dark:bg-darklight
                                shadow-lg
                                rounded-xl
                                p-3
                                z-50
                                w-48
                              "
                            >
                              {subItem.subItems.map(
                                (
                                  child,
                                  childIndex
                                ) => (
                                  <Link
                                    key={childIndex}
                                    to={child.href}
                                    className="
                                      block
                                      px-3
                                      py-2
                                      text-sm
                                      rounded-lg
                                      hover:bg-brand-50
                                    "
                                    onClick={() =>
                                      setSubmenuOpen(
                                        false
                                      )
                                    }
                                  >
                                    {/* Child Translation */}
                                    {t(child.label)}
                                  </Link>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </li>
              )
            )}

            {/* Advertisement */}
            <li>
              <img
                loading="lazy"
                src="https://stage.olxforex.com/storage/article/01K0KKVD64CM3E5E4Y50NKPCSK.jpeg"
                alt="ads"
                className="
                  rounded-2xl
                  mx-auto
                  w-100
                  max-w-40
                "
              />
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default HeaderLink;
